<script>
import {
    BCard, BRow, BCol, BCardGroup, BCardTitle, BCardHeader,
} from 'bootstrap-vue'
import {
    CompliantStatementTable, EvidenceReportTable, DeclarationOfConformityTable
} from "@/views/product/components/product-card/verification";
import Instruction from "@/views/product/components/product-card/verification/Instruction.vue";
import {VerificationDocumentType} from "@/libs/product";
import ReplyDocumentPreview from "@/views/request/sent/components/ReplyDocumentPreview.vue";
import downloadProductDocument from "@core/mixins/download-product-document";

export default {
    mixins: [downloadProductDocument],
    components: {
        ReplyDocumentPreview, BCardGroup, BCardTitle, BCardHeader, BCard,
        BRow, BCol, CompliantStatementTable, EvidenceReportTable, DeclarationOfConformityTable, Instruction,
    },
    props: {
        productSection: {
            type: Object,
            default() {
                return {
                    isSafeUseApplicable: null
                };
            }
        },
        section: {
            required: true
        },
        verificationDocuments: {
            required: true,
            type: Array,
        },
        requestId: {
            required: true
        }
    },

    data() {
        return {
            previewFile: null,
        };
    },

    methods: {
        download(document, done) {
            this
                .downloadRepliedDocument(this.requestId, document)
                .then(done);
        },

        preview(document) {
            this.previewFile = document;
            this.$nextTick(() => {
                this.$refs.preview.show();
            });
        },

        getSafeUseEmptyText() {
            if (false === this.isSafeUseApplicable) {
                return this.$t('Component(Text):safe-use-applicability')
            }

            return 'There is no Safe use instructions.';
        },
    },

    computed: {
        isSafeUseApplicable() {
            if (this.productSection) {
                return this.productSection.isSafeUseApplicable;
            }
            return null;
        },
        declarationOfConformityDocuments() {
            return this.section.filter(d => d.type === VerificationDocumentType.DECLARATION_OR_CONFORMITY);
        },

        evidenceReportDocuments() {
            return this.section.filter(d => d.type === VerificationDocumentType.EVIDENCE_REPORT);
        },

        compliantStatementDocuments() {
            return this.section.filter(d => d.type === VerificationDocumentType.COMPLIANT_STATEMENT);
        },

        dissemblingInstruction() {
            return this.section.find(d => d.type === VerificationDocumentType.DISSEMBLING_INSTRUCTIONS);
        },

        safeUseInstruction() {
            return this.section.find(d => d.type === VerificationDocumentType.SAFE_USE_INSTRUCTIONS);
        },

        evidenceReportStyle() {
            let state = 'required';
            if (!this.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT)) {
                state = 'default';
            } else if (this.evidenceReportDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        docStyle() {
            let state = 'required';
            if (!this.verificationDocuments.includes(VerificationDocumentType.DECLARATION_OR_CONFORMITY)) {
                state = 'default';
            } else if (this.declarationOfConformityDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        compliantStatementStyle() {
            let state = 'required';
            if (!this.verificationDocuments.includes(VerificationDocumentType.COMPLIANT_STATEMENT)) {
                state = 'default';
            } else if (this.compliantStatementDocuments.length) {
                state = 'success';
            }
            return this.$store.state.request.sent.replyVariant[state];
        },
        safeUseState() {
            if (!this.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)) {
                return 'default';
            }
            if (false === this.isSafeUseApplicable) {
                return 'success';
            }
            return (this.safeUseInstruction && this.safeUseInstruction.id) ? 'success' : 'required';
        },
        dissemblingState() {
            if (!this.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS)) {
                return 'default';
            }

            return (this.dissemblingInstruction && this.dissemblingInstruction.id) ? 'success' : 'required';
        },

        showEvidenceReport() {
            return this.verificationDocuments.includes(VerificationDocumentType.EVIDENCE_REPORT);
        },

        showInstructions() {
            return this.verificationDocuments.includes(VerificationDocumentType.SAFE_USE_INSTRUCTIONS)
                && this.verificationDocuments.includes(VerificationDocumentType.DISSEMBLING_INSTRUCTIONS);
        }
    }
}
</script>

<template>
    <div>
        <ReplyDocumentPreview
            ref="preview"
            :file="previewFile"
            :request-id="requestId"
        />
        <b-row>
            <div class="mb-1 mt-1 pl-1"><h3>{{ $t('Page(SentRequest):Component(Text):verification') }}</h3></div>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body :border-variant="compliantStatementStyle.border" :bg-variant="compliantStatementStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):compliant-statement-label') }}</b-card-title>
                    </b-card-header>
                    <CompliantStatementTable
                        :documents="compliantStatementDocuments"
                        :empty-message-bg="compliantStatementStyle.alert"
                        :selectable="false"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row>
            <b-col>
                <b-card no-body :border-variant="docStyle.border" :bg-variant="docStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):doc-label') }}</b-card-title>
                    </b-card-header>
                    <DeclarationOfConformityTable
                        :documents="declarationOfConformityDocuments"
                        :selectable="false"
                        :empty-message-bg="docStyle.alert"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row v-if="showEvidenceReport">
            <b-col>
                <b-card no-body :border-variant="evidenceReportStyle.border" :bg-variant="evidenceReportStyle.bg">
                    <b-card-header>
                        <b-card-title>{{ $t('Component(Verification):evidence-report-label') }}</b-card-title>
                    </b-card-header>
                    <EvidenceReportTable
                        :documents="evidenceReportDocuments"
                        :selectable="false"
                        :empty-message-bg="evidenceReportStyle.alert"
                        @download="download"
                        @preview="preview"
                    />
                </b-card>
            </b-col>
        </b-row>
        <b-row class="mb-1" v-if="showInstructions">
            <b-col>
                <b-card-group deck>
                    <Instruction
                        :instruction="safeUseInstruction"
                        :title="$t('Component(Verification):safe-use-label')"
                        :state="safeUseState"
                        :empty-text="getSafeUseEmptyText()"
                    />
                    <Instruction
                        :instruction="dissemblingInstruction"
                        :title="$t('Component(Verification):dissembling-label')"
                        :state="dissemblingState"
                        @download="download"
                        @preview="preview"
                    />
                </b-card-group>
            </b-col>
        </b-row>
    </div>
</template>

<style scoped lang="scss">

</style>
